import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Modal } from "@fragments";
import { registerVerificationHost } from "@helpers/verification";

import useVerificationFlow, { STEPS } from "./hooks/useVerificationFlow";
import VerificationView from "./view";

/**
 * VerificationModal — the host for sensitive-action verification.
 *
 * Mounted once (App.js, inside ProtectedRoutes). It registers itself with the
 * verification helper; every 428 `verification_required` the axios interceptor
 * meets becomes a flow pushed on this stack. Each flow keeps its own controller
 * mounted for its whole life, so a flow that opened a nested one (phone
 * enrollment inside the wallet flow) picks up exactly where it was once the
 * nested flow resolves. Only the top of the stack is open.
 *
 * Resolving a flow (`{ grant, outcome }`) hands the result back to the
 * interceptor, which replays the original request with the grant.
 */
export default function VerificationModal() {
    const [flows, setFlows] = useState([]);
    const flowsRef = useRef(flows);
    flowsRef.current = flows;

    useEffect(() => {
        const unregister = registerVerificationHost({
            start: (flow) => setFlows((prev) => [...prev, flow]),
        });

        return () => {
            unregister();
            // The host is going away (route tree remount): nothing can finish
            // these flows any more, so release their callers instead of hanging.
            flowsRef.current.forEach((flow) =>
                flow.resolve({ grant: null, outcome: "cancelled" }),
            );
        };
    }, []);

    const handleFinish = useCallback((flow, result) => {
        setFlows((prev) => prev.filter((f) => f !== flow));
        flow.resolve(result);
    }, []);

    const top = flows[flows.length - 1];

    return flows.map((flow) => (
        <VerificationFlowModal
            key={flow.id}
            flow={flow}
            active={flow === top}
            onFinish={handleFinish}
        />
    ));
}

// One entry of the stack: the flow's state (hook) plus its Modal. Lives for
// the flow's whole life; `active` only decides whether its surface is open.
function VerificationFlowModal({ flow, active, onFinish }) {
    const { t } = useTranslation("common");
    const state = useVerificationFlow({ flow, onFinish });

    const locked = state.step === STEPS.LOCKED;
    const success = state.codeStatus === "success";

    let title;
    let subtitle;

    if (locked) {
        title = t("verification.locked.title");
    } else if (state.step === STEPS.CODE) {
        title = t("verification.code.title");
        subtitle = t("verification.code.subtitle", { target: state.target || "" });
    } else {
        title = t("verification.method.title");
        subtitle = t("verification.method.subtitle", {
            action: t(`verification.actions.${state.actionKey}`),
        });
    }

    return (
        <Modal
            open={active}
            onClose={state.cancel}
            size={368}
            className="verification-modal"
            eyebrow={t("verification.eyebrow")}
            title={title}
            subtitle={subtitle}
            hideClose={success}
            footer={
                locked ? (
                    <Button color="inverse" fullWidth onClick={state.acknowledgeLock}>
                        {t("common:actions.understood")}
                    </Button>
                ) : undefined
            }
        >
            <VerificationView {...state} />
        </Modal>
    );
}
